import {
  Bed,
  BedDouble,
  Building2,
  ClipboardList,
  Grid3x3,
  Handshake,
  LayoutDashboard,
  Megaphone,
  Package,
  PanelsTopLeft,
  Settings,
  Shirt,
  UserCog,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { CanAccess, useGetIdentity, useTranslate } from "ra-core";
import { Link, matchPath, useLocation, useSearchParams } from "react-router";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { ProductLine } from "../jobs/jobLines";

const LINE_ICONS: Record<ProductLine, LucideIcon> = {
  curtain: PanelsTopLeft,
  mattress: Bed,
  bedding: BedDouble,
  screen: Grid3x3,
  dryer: Shirt,
};

export const Sidebar = () => {
  const translate = useTranslate();
  const { title } = useConfigurationContext();
  const { identity } = useGetIdentity();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const onJobs = !!matchPath("/jobs/*", location.pathname);
  const activeLine = onJobs ? searchParams.get("line") : null;
  const isActive = (pattern: string) => !!matchPath(pattern, location.pathname);

  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col justify-between overflow-y-auto border-r bg-sidebar shadow-sm md:flex">
      <div className="flex flex-col">
        <Link
          to="/"
          className="flex h-14 items-center gap-2 border-b px-3 text-sidebar-foreground no-underline"
        >
          <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
            HK
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-base font-semibold leading-tight">
              {title}
            </span>
            <span className="truncate text-[10px] text-muted-foreground">
              {translate("crm.nav.tagline")}
            </span>
          </div>
        </Link>

        <SidebarSection label={translate("crm.nav.section_lines")}>
          {(Object.keys(LINE_ICONS) as ProductLine[]).map((line) => (
            <SidebarLink
              key={line}
              to={`/jobs?line=${line}`}
              icon={LINE_ICONS[line]}
              label={translate(`crm.nav.lines.${line}`)}
              active={activeLine === line}
            />
          ))}
        </SidebarSection>

        <SidebarSection label={translate("crm.nav.section_admin")}>
          <SidebarLink
            to="/"
            icon={LayoutDashboard}
            label={translate("crm.nav.dashboard")}
            active={location.pathname === "/"}
          />
          <SidebarLink
            to="/jobs"
            icon={ClipboardList}
            label={translate("crm.nav.jobs")}
            active={onJobs && !activeLine}
          />
          <SidebarLink
            to="/contacts"
            icon={Users}
            label={translate("crm.nav.contacts")}
            active={isActive("/contacts/*")}
          />
          <SidebarLink
            to="/companies"
            icon={Building2}
            label={translate("crm.nav.companies")}
            active={isActive("/companies/*")}
          />
          <SidebarLink
            to="/deals"
            icon={Handshake}
            label={translate("crm.nav.deals")}
            active={isActive("/deals/*")}
          />
          <SidebarLink
            icon={Package}
            label={translate("crm.nav.price_book")}
            soon
          />
          <SidebarLink
            icon={Megaphone}
            label={translate("crm.nav.marketing")}
            soon
          />
          <SidebarLink
            icon={Wallet}
            label={translate("crm.nav.finance")}
            soon
          />
          <CanAccess resource="sales" action="list">
            <SidebarLink
              to="/sales"
              icon={UserCog}
              label={translate("crm.nav.users")}
              active={isActive("/sales/*")}
            />
          </CanAccess>
          <CanAccess resource="configuration" action="edit">
            <SidebarLink
              to="/settings"
              icon={Settings}
              label={translate("crm.nav.settings")}
              active={isActive("/settings")}
            />
          </CanAccess>
        </SidebarSection>
      </div>

      {identity ? (
        <Link
          to="/profile"
          className="flex items-center gap-2 border-t px-3 py-3 text-sidebar-foreground no-underline hover:bg-secondary"
        >
          <div className="flex size-8 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
            {initials(identity.fullName)}
          </div>
          <span className="truncate text-sm font-medium">
            {identity.fullName}
          </span>
        </Link>
      ) : null}
    </aside>
  );
};

const initials = (name?: string) =>
  (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(-2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

const SidebarSection = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="px-3 py-2">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </span>
    <nav className="mt-1.5 flex flex-col gap-0.5">{children}</nav>
  </div>
);

const SidebarLink = ({
  to,
  icon: Icon,
  label,
  active = false,
  soon = false,
}: {
  to?: string;
  icon: LucideIcon;
  label: string;
  active?: boolean;
  soon?: boolean;
}) => {
  const translate = useTranslate();
  const className = cn(
    "flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm no-underline transition-colors",
    active
      ? "bg-sidebar-accent font-semibold text-sidebar-accent-foreground"
      : "text-sidebar-foreground/80 hover:bg-secondary hover:text-sidebar-foreground",
    soon && "cursor-default opacity-60 hover:bg-transparent",
  );
  const content = (
    <>
      <Icon
        className={cn(
          "size-[18px] shrink-0",
          !active && "text-muted-foreground",
        )}
      />
      <span className="truncate">{label}</span>
      {soon ? (
        <span className="ml-auto shrink-0 whitespace-nowrap rounded bg-muted px-1 text-[10px] text-muted-foreground">
          {translate("crm.nav.coming_soon")}
        </span>
      ) : null}
    </>
  );
  if (soon || !to) {
    return (
      <span className={className} aria-disabled="true">
        {content}
      </span>
    );
  }
  return (
    <Link
      to={to}
      className={className}
      aria-current={active ? "page" : undefined}
    >
      {content}
    </Link>
  );
};
